import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Citation } from '../../views/AIAgent/AIAgentResultCitations/AIAgentResultCitations'

import { NewEmailResult } from '../../models/NewEmail'

import { hasTenMinutesPassed } from '../utils'
import { RootState } from '../store'
import { isNewEmailNoResult } from '../../views/EmailV2/NewEmail/utils'
import { User } from '../../models/User'

interface NewEmailState {
  newEmailResult: NewEmailResult | null
  createdAt: string | null
  editingNewEmail: boolean
  editingIsLoading: boolean
  interactionIsLoading: boolean
  executeIsLoading: boolean
  aiAgentId: string | null
  citations: Citation[] | null
  citationIsLoading: boolean
  recipientContact: User | null
  relatedContact: User | null
  contactLoading: boolean
}

const initialState: NewEmailState = {
  newEmailResult: null,
  createdAt: null,
  editingNewEmail: false,
  editingIsLoading: false,
  interactionIsLoading: false,
  executeIsLoading: false,
  aiAgentId: null,
  citations: null,
  citationIsLoading: false,
  recipientContact: null,
  relatedContact: null,
  contactLoading: false,
}

export const newEmailSlice = createSlice({
  name: 'newEmail',
  initialState,
  reducers: {
    setNewEmailResult: (state, action: PayloadAction<NewEmailResult>) => {
      state.newEmailResult = { ...state.newEmailResult, ...action.payload }
      state.createdAt = new Date().toISOString()
    },
    clearNewEmailResult: (state) => {
      state.newEmailResult = null
      state.createdAt = null
      state.citations = null
    },
    setEditingNewEmail: (state, action: PayloadAction<boolean>) => {
      state.editingNewEmail = action.payload
    },
    setEditingIsLoading: (state, action: PayloadAction<boolean>) => {
      state.editingIsLoading = action.payload
    },
    setNewEmailInteractionLoading: (state, action: PayloadAction<boolean>) => {
      state.interactionIsLoading = action.payload
    },
    setNewEmailExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.executeIsLoading = action.payload
    },
    setNewEmailAiAgentId: (state, action: PayloadAction<string>) => {
      state.aiAgentId = action.payload
    },
    setNewEmailCitations: (state, action: PayloadAction<Citation[]>) => {
      state.citations = action.payload
    },
    setNewEmailCitationLoading: (state, action: PayloadAction<boolean>) => {
      state.citationIsLoading = action.payload
    },
    setNewEmailRecipientContact: (state, action: PayloadAction<User | null>) => {
      state.recipientContact = action.payload
    },
    setNewEmailRelatedContact: (state, action: PayloadAction<User | null>) => {
      state.relatedContact = action.payload
    },
    setNewEmailContactLoading: (state, action: PayloadAction<boolean>) => {
      state.contactLoading = action.payload
    },
    resetNewEmail: () => initialState,
  },
})

export const {
  setNewEmailResult,
  clearNewEmailResult,
  setEditingNewEmail,
  setEditingIsLoading,
  setNewEmailInteractionLoading,
  setNewEmailExecutionLoading,
  setNewEmailAiAgentId,
  setNewEmailCitations,
  setNewEmailCitationLoading,
  setNewEmailRecipientContact,
  setNewEmailRelatedContact,
  setNewEmailContactLoading,
  resetNewEmail,
} = newEmailSlice.actions

export const selectNewEmailResult = (state: RootState) => state.emailHub.newEmail.newEmailResult

export const selectNewEmailResultCreatedAt = (state: RootState) => state.emailHub.newEmail.createdAt

export const selectNewEmailEditing = (state: RootState) => state.emailHub.newEmail.editingNewEmail

export const selectNewEmailEditingLoading = (state: RootState) => state.emailHub.newEmail.editingIsLoading

export const selectNewEmailInteractionLoading = (state: RootState) => state.emailHub.newEmail.interactionIsLoading

export const selectNewEmailExecutionLoading = (state: RootState) => state.emailHub.newEmail.executeIsLoading

export const selectNewEmailAiAgentId = (state: RootState) => state.emailHub.newEmail.aiAgentId

export const selectNewEmailCitations = (state: RootState) => state.emailHub.newEmail.citations

export const selectNewEmailCitationIsLoading = (state: RootState) => state.emailHub.newEmail.citationIsLoading

export const selectNewEmailRecipientContact = (state: RootState) => state.emailHub.newEmail.recipientContact

export const selectNewEmailRelatedContact = (state: RootState) => state.emailHub.newEmail.relatedContact

export const selectNewEmailContactLoading = (state: RootState) => state.emailHub.newEmail.contactLoading

export const selectShouldPollNewEmailCitations = createSelector(
  [selectNewEmailResult, selectNewEmailResultCreatedAt, selectNewEmailCitations],
  (newEmailResult, createdAt, citations) => {
    if (!newEmailResult || isNewEmailNoResult(newEmailResult) || !createdAt || citations) {
      return false
    }
    return !hasTenMinutesPassed(createdAt)
  },
)
